import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AIGenerateButton from "../../../../Component/AIGenerateButton/AIGenerateButton";
import Spinner from "../../../../Component/Spinner/Spinner";
import MUIButton from "../../../../Component/UI/MUIButton/MUIButton";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../../redux/actions/metricActions";
import { getSourcesWithAlias } from "../../../../services/apiService";
import { createDomainUrlMapping } from "../ActionComponent/ActionModal";
import EmptyView from "../../../../Component/EmptyView/EmptyView";

const ProgressBarContainer = styled(Box)(({ theme }) => ({
  // backgroundColor: "#eff4f8",
  width: "100%",
  height: "11px",
  borderRadius: "12px",
  overflow: "hidden",
  position: "relative",
  border: "solid 0.5px lightgrey",
}));

const ProgressBar = styled(Box)(({ theme, color, width }) => ({
  backgroundColor: color,
  width: `${width}%`,
  height: "11px",
  borderRadius: "12px",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme?.palette?.common?.white,
  fontWeight: "bold",
  fontSize: "0.9rem",
}));

const CustomTooltip = styled(Tooltip)(() => ({
  tooltip: {
    backgroundColor: "transparent",
    boxShadow: "none",
  },
}));

const AttributeSection = ({
  attributes,
  color,
  type,
  saveActions,
  actionStatement,
  selectedInput,
  isLoading,
}) => {

  if (!attributes || attributes.length === 0) {
    return <EmptyView message="Data Not Available" />;
  }

  return (
    <Grid container spacing={2}>
      {attributes.map((attr, index) => (
        <Grid item xs={12} key={index}>
          <Typography variant="body1" gutterBottom>
            {attr.name}
          </Typography>
          <CustomTooltip
            title={
              <div
                style={{
                  backgroundColor: "#1F1A5F",
                  padding: "15px",
                  margin: "-10px",
                  borderRadius: "15px",
                  color: "#fff",
                }}
              >
                <p>
                  <strong>Category : {attr.name || ""}</strong> <br />
                  <strong>Signal Strength : {attr.value || ""}</strong>
                </p>
                <p>Attributes:</p>
                <ul>
                  {attr?.altNames?.map((altName, index) => (
                    <li key={index}>{altName}</li>
                  ))}
                </ul>
                {type == "negativeAtt" && (
                  <>
                    <hr />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        // marginRight: "8px",
                      }}
                    >
                      {isLoading ? (
                        <Spinner />
                      ) : (
                        <MUIButton
                          size="small"
                          // color= "white"
                          variant="outlined"
                          onClick={() => {
                            saveActions(
                              actionStatement(
                                attr.name,
                                selectedInput?.brand || selectedInput?.product
                              )
                            );
                          }}
                        >
                          <span
                            style={{
                              color: "white",
                              cursor: "pointer",
                            }}
                          >
                            Add To Action
                          </span>
                        </MUIButton>
                      )}
                    </div>
                  </>
                )}
              </div>
            }
          >
            {attr?.value >= 0 && (
              <ProgressBarContainer>
                <ProgressBar color={color} width={attr?.value} />
              </ProgressBarContainer>
            )}
          </CustomTooltip>
        </Grid>
      ))}
    </Grid>
  )
};

const AttributeBars = ({
  errors,
  loading,
  summaries,
  getAttributesSummary,
  negativeAttributes,
  positiveAttributes,
  selectedInput,
  sources,
  customerId,
  sourceURLIds,
  hideSummary = false
}) => {

  const dispatch = useDispatch();

  console.log("errors:", errors);
  console.log("loading:", loading);
  console.log("summaries:", summaries);
  console.log("getAttributesSummary:", getAttributesSummary);
  console.log("negativeAttributes:", negativeAttributes);
  console.log("positiveAttributes:", positiveAttributes);
  console.log("selectedInput:", selectedInput);
  console.log("sources:", sources);
  console.log("customerId:", customerId);
  console.log("sourceURLIds:", sourceURLIds);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const saveActions = async (title) => {
    const llmSources =
      sources && Object.values(sources)?.[0]
        ? Object.keys(Object.values(sources)[0]).slice(0, 5)
        : [];

    setIsLoading(true);

    try {
      // Fetch full URLs and domain mappings
      let sourceFullUrls = [];
      if (llmSources.length > 0) {
        sourceFullUrls = await getSourcesWithAlias({
          sources: llmSources,
          sourceAliasIds: sourceURLIds,
        });
      }
      const domainUrlMapping = createDomainUrlMapping(sourceFullUrls?.data);

      const modifiedObj = {
        title: title,
        rationale: title,
        brand: selectedInput?.brand || selectedInput?.product,
        persona: selectedInput?.persona,
        geography: selectedInput?.geography,
        market: selectedInput?.market,
        category: selectedInput?.category,
        llm: selectedInput?.llm,
        isProduct: selectedInput?.product ? true : false,
        sources: llmSources.map((url) => ({
          domain_url: url,
          fullUrl: domainUrlMapping?.[url] || "",
        })),
      };

      // Create the payload
      const payload = {
        action_json: [modifiedObj],
        is_archived: 0,
        is_active: 1,
      };

      // Dispatch the createActionMetric and fetchActionMetrics
      dispatch(createActionMetric(customerId, payload))
        .then(() => {
          dispatch(fetchActionMetrics(customerId))
            .then(() => {
              toast.success("Action Added!", { autoClose: 700 });
            })
            .catch(() => {
              toast.error("Failed to fetch updated metrics!", {
                autoClose: 700,
              });
            });
        })
        .catch(() => {
          toast.error("Something went wrong while creating the action!", {
            autoClose: 700,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
      toast.error("Failed to fetch source URLs or process the action!", {
        autoClose: 700,
      });
    }
  };

  return (
    <div className="col-md-12 mt-4">
      <div className="card p-3 custCard">
        <div className="row">
          <div className={!hideSummary ? "col-md-8" : "col-md-12"} >
            <Box sx={{ maxWidth: "900px", mx: "auto", p: 2 }}>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Typography variant="h6" align="left" gutterBottom>
                    Positive Attributes
                  </Typography>
                  <hr />
                  <AttributeSection
                    attributes={positiveAttributes.sort(
                      (a, b) => b.value - a.value
                    )}
                    color="#C7F0EC"
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="h6" align="left" gutterBottom>
                    Negative Attributes
                  </Typography>
                  <hr />
                  <AttributeSection
                    attributes={negativeAttributes?.sort(
                      (a, b) => b.value - a.value
                    )}
                    color="#FEE7E6"
                    type="negativeAtt"
                    sources={sources}
                    saveActions={saveActions}
                    actionStatement={(attribute, brandProduct) =>
                      `${attribute} is a Negative Attribute of the ${brandProduct}`
                    }
                    selectedInput={selectedInput}
                    isLoading={isLoading}
                  />
                </Grid>
              </Grid>
            </Box>
          </div>
          {!hideSummary &&
            <div className="col-md-4">
              <div className="graphGreyBg">
                <p className="mb-3 font14-600">
                  Summary &nbsp; &nbsp;
                  <AIGenerateButton
                    fetchSummary={getAttributesSummary}
                    type="attributes"
                  />
                </p>
                <div className="graphsummary font-11">
                  {loading?.attributes ? (
                    <Spinner />
                  ) : summaries?.attributes ? (
                    <p className="mb-1"> {summaries?.attributes} </p>
                  ) : errors?.attributes ? (
                    <p style={{ color: "red", textAlign: "center" }}>
                      {errors?.attributes}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AttributeBars;
